import React, { useState, createContext, useContext, useMemo, ReactNode } from 'react';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';

// Time filter periods
export type TimePeriod = 'YTD' | 'MTD' | 'L12M' | 'HISTORIC' | 'RANGE';

export interface TimeFilter {
  type: string;
  key: TimePeriod;
  value: string;
}

export interface FilterItem {
  type: string;
  key: string;
  value: string;
  id: string;
}

export interface DateRange {
  fromDate: string;
  toDate: string;
}

export interface QueryData {
  licensee_ids: string[];
  subCustomer_ids: string[];
  subCompany_ids: string[];
  branch_ids: string[];
  originCountry: string[];
  destinationCountry: string[];
  originCity: string[];
  destinationCity: string[];
  transportType: string[];
  range?: DateRange;
}

interface FiltersContextType {
  // Time filter
  timeFilter: TimeFilter;
  setTimeFilter: (filter: TimeFilter) => void;
  changeTimeFilter: (filterItem: { key: TimePeriod; value: string }) => boolean;
  fromToDate?: DateRange;
  setFromToDate: (dateRange?: DateRange) => void;

  // Licensee (checklist - multiple selections for Master Admin)
  licenseeData: FilterItem[];
  setLicenseeData: (data: FilterItem[]) => void;

  // Sub Company (checklist - multiple selections)
  subCompanyData: FilterItem[];
  setSubCompanyData: (data: FilterItem[]) => void;

  // Branch (checklist - multiple selections)
  branchData: FilterItem[];
  setBranchData: (data: FilterItem[]) => void;

  // Sub Customer (checklist - multiple selections)
  subCustomerData: FilterItem[];
  setSubCustomerData: (data: FilterItem[]) => void;

  // Country filters
  countryOriginData: FilterItem[];
  setCountryOriginData: (data: FilterItem[]) => void;
  countryDestinationData: FilterItem[];
  setCountryDestinationData: (data: FilterItem[]) => void;

  // City filters
  cityOriginData: FilterItem[];
  setCityOriginData: (data: FilterItem[]) => void;
  cityDestinationData: FilterItem[];
  setCityDestinationData: (data: FilterItem[]) => void;

  // Transport mode filter
  transportModeData: FilterItem[];
  setTransportModeData: (data: FilterItem[]) => void;

  // Helper functions
  isPresent: (filterItem: FilterItem, filterKey: string) => boolean;
  addFilter: (filterItem: FilterItem, filterType: string, filterKey: string) => void;
  removeFilter: (filterItem: FilterItem, filterKey: string) => void;
  handleRadioSelect: (radioItem: FilterItem, filterType: string, filterKey: string) => void;
  clearAllType: (key: string) => void;
  getQueryData: () => QueryData;
  getPanelQueryData: () => any;
  removedTrigger: boolean;
  setRemovedTrigger: (trigger: boolean) => void;
  buildFilterPayload: (overrides?: any) => any;
}

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

const ids = (items: FilterItem[]): string[] => items.map((item) => item.id);

// Pure: maps populated context filter arrays onto payload keys.
const applyFilterArrays = (
  payload: any,
  filters: {
    licenseeData: FilterItem[];
    subCompanyData: FilterItem[];
    branchData: FilterItem[];
    subCustomerData: FilterItem[];
    countryOriginData: FilterItem[];
    countryDestinationData: FilterItem[];
    cityOriginData: FilterItem[];
    cityDestinationData: FilterItem[];
    transportModeData: FilterItem[];
  }
) => {
  if (filters.licenseeData.length > 0) {
    payload.companyId = filters.licenseeData[0].id;
    if (filters.licenseeData.length > 1) {
      payload.companyIds = ids(filters.licenseeData);
    }
  }
  if (filters.subCompanyData.length > 0) payload.subCompany_ids = ids(filters.subCompanyData);
  if (filters.branchData.length > 0) payload.branch_ids = ids(filters.branchData);
  if (filters.subCustomerData.length > 0) payload.subCustomer_ids = ids(filters.subCustomerData);
  if (filters.countryOriginData.length > 0) payload.originCountry = ids(filters.countryOriginData);
  if (filters.countryDestinationData.length > 0) payload.destinationCountry = ids(filters.countryDestinationData);
  if (filters.cityOriginData.length > 0) payload.originCity = ids(filters.cityOriginData);
  if (filters.cityDestinationData.length > 0) payload.destinationCity = ids(filters.cityDestinationData);
  if (filters.transportModeData.length > 0) payload.transportMode = ids(filters.transportModeData);
};

// Pure: defaults a scope id list from a user value when not already populated.
const defaultScope = (payload: any, idsKey: string, value?: string) => {
  if (value && (!payload[idsKey] || payload[idsKey].length === 0)) {
    payload[idsKey] = [value];
  }
};

// Pure: applies role-specific branch/subCompany defaults onto the payload.
const applyRoleDefaults = (payload: any, user: any) => {
  if (!user) return;
  if (user.accountType === AccountType.BranchAdmin) {
    if (user.branch) {
      payload.branch = user.branch;
      defaultScope(payload, 'branch_ids', user.branch);
    }
    if (user.subCompany) {
      payload.sub_company = user.subCompany;
      defaultScope(payload, 'subCompany_ids', user.subCompany);
    }
  } else if (user.accountType === AccountType.SubCustomerAdmin) {
    if (user.subCompany) {
      payload.sub_company = user.subCompany;
      defaultScope(payload, 'subCompany_ids', user.subCompany);
    }
  }
};

interface FiltersProviderProps {
  children: ReactNode;
  initialTimeFilter?: TimeFilter;
  initialFromToDate?: DateRange;
  initialLicenseeData?: FilterItem[];
  initialSubCompanyData?: FilterItem[];
  initialBranchData?: FilterItem[];
  initialSubCustomerData?: FilterItem[];
  initialCountryOriginData?: FilterItem[];
  initialCountryDestinationData?: FilterItem[];
  initialCityOriginData?: FilterItem[];
  initialCityDestinationData?: FilterItem[];
  initialTransportModeData?: FilterItem[];
}

export const FiltersProvider: React.FC<FiltersProviderProps> = ({
  children,
  initialTimeFilter,
  initialFromToDate,
  initialLicenseeData,
  initialSubCompanyData,
  initialBranchData,
  initialSubCustomerData,
  initialCountryOriginData,
  initialCountryDestinationData,
  initialCityOriginData,
  initialCityDestinationData,
  initialTransportModeData,
}) => {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(
    initialTimeFilter ?? { type: 'Time', key: 'L12M', value: 'L12M' }
  );

  const [fromToDate, setFromToDate] = useState<DateRange | undefined>(initialFromToDate);
  const [removedTrigger, setRemovedTrigger] = useState(false);
  const { user } = useAuthStore();

  const [licenseeData, setLicenseeData] = useState<FilterItem[]>(initialLicenseeData ?? []);
  const [subCompanyData, setSubCompanyData] = useState<FilterItem[]>(initialSubCompanyData ?? []);
  const [branchData, setBranchData] = useState<FilterItem[]>(initialBranchData ?? []);
  const [subCustomerData, setSubCustomerData] = useState<FilterItem[]>(initialSubCustomerData ?? []);
  const [countryOriginData, setCountryOriginData] = useState<FilterItem[]>(initialCountryOriginData ?? []);
  const [countryDestinationData, setCountryDestinationData] = useState<FilterItem[]>(initialCountryDestinationData ?? []);
  const [cityOriginData, setCityOriginData] = useState<FilterItem[]>(initialCityOriginData ?? []);
  const [cityDestinationData, setCityDestinationData] = useState<FilterItem[]>(initialCityDestinationData ?? []);
  const [transportModeData, setTransportModeData] = useState<FilterItem[]>(initialTransportModeData ?? []);

  const getAppropriateData = (type: string): any => {
    switch (type) {
      case 'licensee':
        return licenseeData;
      case 'subCompany':
        return subCompanyData;
      case 'branch':
        return branchData;
      case 'subCustomer':
        return subCustomerData;
      case 'countryOrigin':
        return countryOriginData;
      case 'countryDestination':
        return countryDestinationData;
      case 'cityOrigin':
        return cityOriginData;
      case 'cityDestination':
        return cityDestinationData;
      case 'transportMode':
        return transportModeData;
      default:
        return null;
    }
  };

  const setAppropriateData = (item: any, type: string) => {
    switch (type) {
      case 'licensee':
        setLicenseeData([...item]);
        break;
      case 'subCompany':
        setSubCompanyData([...item]);
        break;
      case 'branch':
        setBranchData([...item]);
        break;
      case 'subCustomer':
        setSubCustomerData([...item]);
        break;
      case 'countryOrigin':
        setCountryOriginData([...item]);
        break;
      case 'countryDestination':
        setCountryDestinationData([...item]);
        break;
      case 'cityOrigin':
        setCityOriginData([...item]);
        break;
      case 'cityDestination':
        setCityDestinationData([...item]);
        break;
      case 'transportMode':
        setTransportModeData([...item]);
        break;
    }
  };

  const isPresent = (filterItem: FilterItem, filterKey: string): boolean => {
    const filterData = getAppropriateData(filterKey);
    if (!filterData) return false;

    if (Array.isArray(filterData) && filterData.length > 0) {
      return filterData.some(
        (existingFilterItem) =>
          existingFilterItem.key === filterKey && filterItem.id === existingFilterItem.id
      );
    }
    return false;
  };

  const addFilter = (filterItem: FilterItem, filterType: string, filterKey: string) => {
    if (filterKey) {
      const newFilters = getAppropriateData(filterKey);
      if (newFilters) {
        const newFilterItem = {
          type: filterType,
          key: filterKey,
          value: filterItem.value,
          id: filterItem.id
        };
        newFilters.push(newFilterItem);
        setAppropriateData(newFilters, filterKey);
      }
    }
  };

  const removeFilter = (filterItem: FilterItem, filterKey: string) => {
    if (filterKey) {
      let newFilters = getAppropriateData(filterKey);

      if (Array.isArray(newFilters) && newFilters.length > 0) {
        newFilters = newFilters.filter(
          (existingFilterItem) => filterItem.id !== existingFilterItem.id
        );
        setAppropriateData(newFilters, filterKey);
        setRemovedTrigger(!removedTrigger);
      } else {
        setAppropriateData([], filterKey);
        setRemovedTrigger(!removedTrigger);
      }
    }
  };

  const changeTimeFilter = (filterItem: { key: TimePeriod; value: string }): boolean => {
    if (filterItem.key === 'RANGE') {
      const newTimeFilter = { ...timeFilter };
      newTimeFilter.key = filterItem.key;
      newTimeFilter.value = filterItem.value;
      setTimeFilter(newTimeFilter);
      return true;
    } else {
      const newTimeFilter = {
        type: 'Time',
        key: filterItem.key,
        value: filterItem.value
      };
      setTimeFilter(newTimeFilter);
      if (fromToDate) {
        setFromToDate(undefined);
      }
      return true;
    }
  };

  const handleRadioSelect = (radioItem: FilterItem, filterType: string, filterKey: string) => {
    const newFilterItem = {
      type: filterType,
      key: filterKey,
      id: radioItem.id,
      value: radioItem.value
    };
    setAppropriateData([newFilterItem], filterKey);
  };

  const clearAllType = (key: string) => {
    let newFilters: FilterItem[] = [];
    const oldFilters = getAppropriateData(key);

    if (Array.isArray(oldFilters) && oldFilters.length) {
      oldFilters.forEach((filterItem) => {
        if (filterItem.key !== key) {
          newFilters.push(filterItem);
        }
      });
      setAppropriateData(newFilters, key);
    }
  };

  const getQueryData = (): QueryData => {
    const queryData: QueryData = {
      licensee_ids: licenseeData.map((item) => item.id),
      subCustomer_ids: subCustomerData.map((item) => item.id),
      subCompany_ids: subCompanyData.map((item) => item.id),
      branch_ids: branchData.map((item) => item.id),
      originCountry: countryOriginData.map((item) => item.id),
      destinationCountry: countryDestinationData.map((item) => item.id),
      originCity: cityOriginData.map((item) => item.id),
      destinationCity: cityDestinationData.map((item) => item.id),
      transportType: transportModeData.map((item) => item.id)
    };

    if (timeFilter.key === 'RANGE' && fromToDate) {
      queryData.range = fromToDate;
    }

    return queryData;
  };

  const getPanelQueryData = () => {
    return {
      licensee_ids: licenseeData.map((item) => item.id),
      subCustomer_ids: subCustomerData.map((item) => item.id),
      subCompany_ids: subCompanyData.map((item) => item.id),
      branch_ids: branchData.map((item) => item.id),
      origincountry: countryOriginData.map((item) => item.id),
      destinationcountry: countryDestinationData.map((item) => item.id),
      origincity: cityOriginData.map((item) => item.id),
      destinationcity: cityDestinationData.map((item) => item.id),
      transporttype: transportModeData.map((item) => item.id)
    };
  };

  const buildFilterPayload = (overrides: any = {}) => {
    const payload: any = {};

    // Global filters from context
    applyFilterArrays(payload, {
      licenseeData,
      subCompanyData,
      branchData,
      subCustomerData,
      countryOriginData,
      countryDestinationData,
      cityOriginData,
      cityDestinationData,
      transportModeData
    });

    if (timeFilter) {
      payload.type = timeFilter.key;
    }

    if (fromToDate) {
      payload.from_date = fromToDate.fromDate;
      payload.to_date = fromToDate.toDate;
    }

    // Role-specific context logic (from Dashboard.tsx)
    applyRoleDefaults(payload, user);

    // Merge overrides
    return { ...payload, ...overrides };
  };

  const value = useMemo(
    () => ({
      timeFilter,
      setTimeFilter,
      fromToDate,
      setFromToDate,
      removedTrigger,
      setRemovedTrigger,
      licenseeData,
      setLicenseeData,
      subCompanyData,
      setSubCompanyData,
      branchData,
      setBranchData,
      subCustomerData,
      setSubCustomerData,
      countryOriginData,
      setCountryOriginData,
      countryDestinationData,
      setCountryDestinationData,
      cityOriginData,
      setCityOriginData,
      cityDestinationData,
      setCityDestinationData,
      transportModeData,
      setTransportModeData,
      isPresent,
      addFilter,
      removeFilter,
      changeTimeFilter,
      getQueryData,
      getPanelQueryData,
      handleRadioSelect,
      clearAllType,
      buildFilterPayload
    }),
    [
      timeFilter,
      fromToDate,
      removedTrigger,
      licenseeData,
      subCompanyData,
      branchData,
      subCustomerData,
      countryOriginData,
      countryDestinationData,
      cityOriginData,
      cityDestinationData,
      transportModeData,
      isPresent,
      addFilter,
      removeFilter,
      changeTimeFilter,
      getQueryData,
      getPanelQueryData,
      handleRadioSelect,
      clearAllType,
      buildFilterPayload
    ]
  );

  return (
    <FiltersContext.Provider value={value}>
      {children}
    </FiltersContext.Provider>
  );
};

export const useFilters = () => {
  const context = useContext(FiltersContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
};
